/* Route-title context — feeds the topbar breadcrumb with both:
 *
 *   - title       a single string label appended as the last crumb
 *                 (legacy; used by ChatView for the active chat name)
 *   - subCrumbs   an ordered list of {label, href} segments appended
 *                 *between* the route name and the deepest crumb.
 *                 Used by views with selection state — Agents (tab
 *                 / selected agent / selected run), the Admin
 *                 dashboard (active tab), the directory views (open
 *                 person / project / client), etc.
 *
 * Pages call useSubCrumbs([...]) inside their render to declare what
 * the current crumb trail should be. The hook replaces the previous
 * trail on every change and clears it on unmount.
 */
import React from 'react';

const Ctx = React.createContext({
  title: '',
  setTitle: () => {},
  subCrumbs: [],
  setSubCrumbs: () => {},
});

export const RouteTitleProvider = ({ children }) => {
  const [title, setTitle] = React.useState('');
  const [subCrumbs, setSubCrumbsState] = React.useState([]);
  // Stable setter that no-ops when the next crumb list is shallow-
  // equal to the current — saves a render when a page re-runs the
  // hook with the same trail (very common on hover/state updates).
  const setSubCrumbs = React.useCallback(next => {
    setSubCrumbsState(prev => {
      const a = prev, b = next || [];
      if (a.length !== b.length) return b;
      for (let i = 0; i < a.length; i++) {
        if (a[i].label !== b[i].label || a[i].href !== b[i].href) return b;
      }
      return prev;
    });
  }, []);
  const value = React.useMemo(
    () => ({ title, setTitle, subCrumbs, setSubCrumbs }),
    [title, subCrumbs, setSubCrumbs],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useRouteTitle = () => React.useContext(Ctx);

/** Declarative sub-crumb hook. Pass an ordered list of
 *  `{label, href}` segments. The previous trail is replaced on every
 *  change and cleared when the component unmounts.
 *
 *  Example — Agents view with a selected agent and a selected run:
 *
 *    useSubCrumbs([
 *      { label: 'Public',           href: 'agents' },
 *      { label: agent.name,         href: null },
 *      { label: `Run ${runId.slice(0, 8)}`, href: null },
 *    ]);
 */
export function useSubCrumbs(crumbs) {
  const { setSubCrumbs } = useRouteTitle();
  // Stringify for the dep array so a same-shape new array reference
  // doesn't trigger an effect re-run.
  const key = JSON.stringify(crumbs);
  React.useEffect(() => {
    setSubCrumbs(crumbs || []);
    return () => setSubCrumbs([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
